import {
  FaChartBar,
  FaDoorOpen,
  FaHome,
  FaPhoneAlt,
  FaRegClock,
  FaUserAlt,
} from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { usePageDateTime } from "../hooks/usePageDateTime";

const headerActionClass =
  "group relative inline-flex h-[34px] w-[44px] items-center justify-center rounded border bg-white transition focus-visible:outline-none focus-visible:ring-2";

const tooltipClass =
  "pointer-events-none absolute right-1/2 top-[calc(100%+0.4rem)] z-50 translate-x-1/2 rounded bg-[#111827] px-2 py-1 text-xs font-medium text-white opacity-0 shadow transition group-hover:opacity-100 group-focus-visible:opacity-100";

function ActionPageLayout({ title, icon, headerStatus, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasModuleAccess, logout, role, user } = useAuth();
  const dateTime = usePageDateTime();
  const displayName = user?.username || user?.email || "User";
  const displayRole = role || "User";
  const headerLinks = [
    {
      moduleKey: "liveDashboard",
      label: "Live Dashboard",
      path: "/live-dashboard",
      icon: FaChartBar,
      className: "border-[#6d4b8a] text-[#6d4b8a] hover:bg-[#f6f0fb] focus-visible:bg-[#f6f0fb] focus-visible:ring-[#d8c1eb]",
    },
    {
      moduleKey: "dailyCallAssignment",
      label: "Daily Call Assignment",
      path: "/telecalling/daily-assignment",
      icon: FaPhoneAlt,
      className: "border-[#1696a7] text-[#087c8b] hover:bg-[#ecfeff] focus-visible:bg-[#ecfeff] focus-visible:ring-[#9ce7ef]",
    },
  ].filter((link) => hasModuleAccess(link.moduleKey));

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <main className="app-scrollbar h-screen overflow-y-auto bg-white px-4 pb-3 pt-[74px] text-[#111827] max-[960px]:pt-[198px]">
      <header className="fixed inset-x-0 top-0 z-50 grid min-h-[74px] grid-cols-[178px_1fr_minmax(295px,auto)] items-center border-b border-[#d9dfe8] bg-[#f7f8fa] px-4 py-2 shadow-sm max-[960px]:grid-cols-1 max-[960px]:justify-items-center max-[960px]:gap-3">
        <Logo compact />

        <div className="flex items-center justify-center gap-3 text-center">
          {icon}
          <h1 className="m-0 text-[1.55rem] font-bold text-[#111827] max-[720px]:text-xl">
            {title}
          </h1>
        </div>

        <div className="justify-self-end max-[960px]:justify-self-center">
          <div className="flex items-center justify-end gap-3 max-[960px]:justify-center">
            <div className="flex shrink-0 items-center gap-2">
              <Link
                aria-label="Dashboard"
                className={`${headerActionClass} border-[#0d73ff] text-[#0d73ff] no-underline hover:bg-[#eff6ff] focus-visible:bg-[#eff6ff] focus-visible:ring-[#9fc8ff]`}
                to="/dashboard"
              >
                <FaHome className="h-4 w-4 flex-none" aria-hidden="true" />
                <span className={tooltipClass}>Dashboard</span>
              </Link>
              {headerLinks.map((link) => {
                const Icon = link.icon;
                const isCurrentPage = location.pathname === link.path;

                return (
                  <Link
                    aria-current={isCurrentPage ? "page" : undefined}
                    aria-label={link.label}
                    className={`${headerActionClass} ${link.className} ${
                      isCurrentPage ? "bg-[#e8f1fb] ring-1 ring-current" : ""
                    }`}
                    key={link.moduleKey}
                    to={link.path}
                  >
                    <Icon className="h-4 w-4 flex-none" aria-hidden="true" />
                    <span className={tooltipClass}>{link.label}</span>
                  </Link>
                );
              })}
              <button
                aria-label="Logout"
                className={`${headerActionClass} border-[#dc3545] text-[#dc3545] hover:bg-[#fff5f5] focus-visible:bg-[#fff5f5] focus-visible:ring-[#ffc2c8]`}
                type="button"
                onClick={handleLogout}
              >
                <FaDoorOpen className="h-4 w-4 flex-none text-[#c98655]" aria-hidden="true" />
                <span className={tooltipClass}>Logout</span>
              </button>
            </div>

            <div className="grid justify-items-end gap-1 text-right max-[960px]:justify-items-center max-[960px]:text-center">
              <div className="flex items-center justify-end gap-2 text-sm font-medium max-[960px]:justify-center">
                <FaUserAlt className="h-3 w-3 flex-none text-[#432071]" aria-hidden="true" />
                <span>
                  {displayName} ({displayRole})
                </span>
              </div>
              <div className="flex items-center justify-end gap-1.5 whitespace-nowrap text-xs text-[#4b5563] max-[960px]:justify-center">
                <FaRegClock className="h-3 w-3 flex-none text-[#64748b]" aria-hidden="true" />
                {dateTime}
              </div>
              {headerStatus ? (
                <div className="whitespace-nowrap text-xs text-[#4b5563]">
                  {headerStatus}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      {children}
    </main>
  );
}

export default ActionPageLayout;
